import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AutobazarListings from '../components/AutobazarListings';
import axios from 'axios';
import { t } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003';

// Interval options every account gets, plus the extras certain accounts have.
// Merged and sorted, so an extra slots into its natural place in the row.
const BASE_INTERVAL_OPTIONS = [2, 3, 4, 5, 7, 14];
const EXTRA_INTERVAL_OPTIONS: Record<string, number[]> = {
  autojokr: [8],
  lerent: [8, 30]
};

const PageContainer = styled.div`
  padding: 16px;
  max-width: 100%;
  
  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  margin-bottom: 8px;
  
  @media (min-width: 768px) {
    font-size: 32px;
    margin-bottom: 10px;
  }
`;

const SubTitle = styled.h2`
  font-size: 16px;
  color: #666;
  margin-bottom: 16px;
  
  @media (min-width: 768px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const VehicleCountHeading = styled.h3`
  font-size: 16px;
  color: #333;
  margin: 20px 0 16px 0;
  font-weight: 600;
  
  @media (min-width: 768px) {
    font-size: 20px;
    margin: 30px 0 20px 0;
  }
`;

const IntervalContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    gap: 5px;
    margin-bottom: 30px;
  }
`;

const IntervalButtonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  
  @media (min-width: 768px) {
    width: auto;
    gap: 5px;
  }
`;

const ActionButtonsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  margin-top: 8px;
  
  @media (min-width: 768px) {
    width: auto;
    margin-top: 0;
    margin-left: 10px;
  }
`;

const IntervalButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  /* Narrow enough that four pills still fit per row on a 320px phone, so even
     the longest row (accounts with the 8 and 30 day settings) wraps tidily */
  padding: 8px 10px;
  background-color: ${props => {
    if (props.disabled) return '#f0f0f0';
    return props.active ? '#333' : 'white';
  }};
  color: ${props => {
    if (props.disabled) return '#999';
    return props.active ? 'white' : '#333';
  }};
  border: 1px solid ${props => props.disabled ? '#e0e0e0' : '#ccc'};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  outline: none;
  min-width: 64px;
  transition: all 0.3s;
  border-radius: 20px;
  opacity: ${props => props.disabled ? 0.7 : 1};
  font-size: 14px;
  flex: 1;

  &:hover {
    background-color: ${props => {
      if (props.disabled) return '#f0f0f0';
      return props.active ? '#555' : '#f5f5f5';
    }};
  }
  
  @media (min-width: 768px) {
    padding: 10px 20px;
    min-width: 100px;
    border-radius: 25px;
    font-size: 16px;
    flex: none;
    margin-right: 5px;
    
    &:first-child {
      border-top-left-radius: 25px;
      border-bottom-left-radius: 25px;
    }

    &:last-child {
      border-top-right-radius: 25px;
      border-bottom-right-radius: 25px;
      margin-right: 0;
    }
  }
`;

const RefreshButton = styled.button<{ active?: boolean }>`
  background-color: ${props => props.active ? '#666' : '#C52126'};
  color: white;
  border: none;
  border-radius: 20px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex: 1;
  min-width: 80px;

  &:hover {
    background-color: ${props => props.active ? '#555' : '#A61D22'};
  }
  
  @media (min-width: 768px) {
    border-radius: 25px;
    padding: 12px 20px;
    font-size: 16px;
    flex: none;
    min-width: auto;
  }
`;

const TurnAllOffButton = styled.button`
  background-color: #d32f2f;
  color: white;
  border: 2px solid #ff0000;
  border-radius: 20px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex: 1;
  min-width: 100px;

  &:hover {
    background-color: #b71c1c;
  }
  
  @media (min-width: 768px) {
    border-radius: 25px;
    padding: 12px 20px;
    font-size: 16px;
    flex: none;
    min-width: auto;
  }
`;

const CarListPage: React.FC = () => {
  const [activeInterval, setActiveInterval] = useState<number | 'off'>(3);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [listingsCount, setListingsCount] = useState<number>(0);
  const [activeListingsCount, setActiveListingsCount] = useState<number>(0);
  const [currentUsername, setCurrentUsername] = useState<string>('');

  const intervalOptions = Array.from(
    new Set([...BASE_INTERVAL_OPTIONS, ...(EXTRA_INTERVAL_OPTIONS[currentUsername] || [])])
  ).sort((a, b) => a - b);

  console.log('CarListPage component rendered - Turn All Off button should be visible');

  // Fetch user settings on component mount
  useEffect(() => {
    async function fetchUserSettings() {
      try {
        setIsLoading(true);
        
        // Get the username from localStorage
        let username = '';
        const storedUser = localStorage.getItem('user');
        
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            username = parsedUser.username || '';
          } catch (err) {
            console.error('Error parsing stored user:', err);
          }
        }
        
        // Fallback to direct username if necessary
        if (!username) {
          username = localStorage.getItem('username') || '';
        }
        
        // Fallback to a test username if we still don't have one
        if (!username) {
          username = 'mtautos';
        }

        setCurrentUsername(username);

        // Fetch user settings
        const response = await axios.get(`${API_URL}/api/user-settings/${username}`);
        
        if (response.data && response.data.success) {
          // Set the active interval based on the response
          setActiveInterval(response.data.update_interval);
          
          // Set disabled state based on whether interval is 'off'
          setIsDisabled(response.data.update_interval === 'off');
        } else {
          console.error('Failed to fetch user settings:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching user settings:', err);
        setError('Failed to load user settings');
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchUserSettings();
  }, []);
  
  // Update user settings when interval changes
  const updateUserSettings = async (interval: number | 'off') => {
    try {
      // Get the username from localStorage
      let username = '';
      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          username = parsedUser.username || '';
        } catch (err) {
          console.error('Error parsing stored user:', err);
        }
      }
      
      // Fallback to direct username if necessary
      if (!username) {
        username = localStorage.getItem('username') || '';
      }
      
      // Fallback to a test username if we still don't have one
      if (!username) {
        username = 'mtautos';
      }
      
      // Update user settings
      const response = await axios.post(`${API_URL}/api/update-interval`, {
        username,
        interval
      });
      
      if (!response.data || !response.data.success) {
        console.error('Failed to update user settings:', response.data?.message);
      }
    } catch (err) {
      console.error('Error updating user settings:', err);
    }
  };
  
  const handleRefreshToggle = () => {
    const newDisabledState = !isDisabled;
    setIsDisabled(newDisabledState);
    
    // If disabling, save 'off' to the database
    // If enabling, save the last active interval (or default to 3)
    const newInterval = newDisabledState ? 'off' : (activeInterval === 'off' ? 3 : activeInterval);
    setActiveInterval(newInterval);
    updateUserSettings(newInterval);
  };
  
  const handleIntervalClick = (days: number) => {
    if (!isDisabled) {
      setActiveInterval(days);
      updateUserSettings(days);
    }
  };
  
  // Function to turn off all listings
  const turnOffAllListings = async () => {
    try {
      console.log('Turn All Off button clicked!');
      
      // Get the username from localStorage
      let username = '';
      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          username = parsedUser.username || '';
        } catch (err) {
          console.error('Error parsing stored user:', err);
        }
      }
      
      // Fallback to direct username if necessary
      if (!username) {
        username = localStorage.getItem('username') || '';
      }

      if (!username) {
        console.error('No username found');
        return;
      }

      // Send the request to turn off all listings
      const response = await axios.post(`${API_URL}/api/turn-off-all-listings`, {
        username
      });

      if (response.data && response.data.success) {
        console.log(`Successfully turned off ${response.data.updatedCount} listings`);
        // Force a refresh of the listings to show the updated state
        window.location.reload();
      } else {
        console.error('Failed to turn off all listings:', response.data?.message);
      }
    } catch (error) {
      console.error('Error turning off all listings:', error);
    }
  };
  
  if (isLoading) {
    return <div>{t('Loading settings...')}</div>;
  }
  
  if (error) {
    return <div>{t(error)}</div>;
  }

  return (
    <PageContainer>
      <PageTitle>{t('Moje autá')}</PageTitle>
      <SubTitle>{t('Interval vymazania a opätovného nahrania')}</SubTitle>
      
      <IntervalContainer>
        <IntervalButtonsRow>
          {intervalOptions.map(days => (
            <IntervalButton
              key={days}
              active={activeInterval === days}
              onClick={() => handleIntervalClick(days)}
              disabled={isDisabled}
            >
              {t(`${days} dni`)}
            </IntervalButton>
          ))}
        </IntervalButtonsRow>
        <ActionButtonsRow>
          <RefreshButton 
            onClick={handleRefreshToggle}
            active={isDisabled}
          >
            {isDisabled ? t('Zapnúť') : t('Vypnúť')}
          </RefreshButton>
          <TurnAllOffButton 
            onClick={turnOffAllListings}
          >
            {t('Vypnúť všetky')}
          </TurnAllOffButton>
        </ActionButtonsRow>
      </IntervalContainer>
      
      <VehicleCountHeading>
        {t('Počet vozidiel')}: {listingsCount} | {t('Aktívne vozidlá')}: {activeListingsCount}
      </VehicleCountHeading>
      
      {/* Display vehicle listings */}
      <AutobazarListings 
        onListingsChange={setListingsCount} 
        onActiveListingsChange={setActiveListingsCount} 
      />
    </PageContainer>
  );
};

export default CarListPage; 